import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient, useModuleAvailability } from "@playbookai/api-clients";
import {
  PLATFORM_MODULE_IDS,
  type PlatformModuleId,
  type PlatformProviderFailure,
  type PlatformSearchResult,
} from "@playbookai/types";
import { useAuthStore } from "../auth/auth-store.js";
import { searchPlatform } from "../api/platform-search-api.js";

const SEARCH_DEBOUNCE_MS = 180;
const NO_RESULTS: readonly PlatformSearchResult[] = [];
const NO_FAILURES: readonly PlatformProviderFailure[] = [];

/** `idle`: no remote search for this query (too short, closed, signed
 *  out). `searching`: the debounce or the request is still running.
 *  `failed`: the request failed. `done`: the results (possibly none) are in.
 *  `searching` and `failed` can come with `results`: TanStack keeps an
 *  earlier answer through a refresh, and through a failed one. The dialog
 *  keeps those rows and shows the state beside them, never instead of them. */
export type PlatformSearchStatus = "idle" | "searching" | "failed" | "done";

export interface PlatformSearchState {
  readonly results: readonly PlatformSearchResult[];
  readonly partialFailures: readonly PlatformProviderFailure[];
  readonly status: PlatformSearchStatus;
  readonly retry: () => void;
}

export function usePlatformSearch(
  query: string,
  open: boolean,
): PlatformSearchState {
  const config = useApiClient();
  const claims = useAuthStore((state) => state.claims);
  // ADR-0100: search only the modules this workspace may use. The server
  // filters too; asking it for the available ones keeps an off module out
  // even before its session list refreshes, and with none left there is
  // nothing to search at all.
  const availability = useModuleAvailability();
  const availableModules = useMemo<readonly PlatformModuleId[]>(
    () => PLATFORM_MODULE_IDS.filter((id) => availability.isAvailable(id)),
    [availability],
  );
  const narrowed = availableModules.length < PLATFORM_MODULE_IDS.length;
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const normalized = query.trim();

  useEffect(() => {
    if (!open || normalized.length < 2) {
      setDebouncedQuery("");
      return;
    }
    const timer = window.setTimeout(
      () => setDebouncedQuery(normalized),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [open, normalized]);

  const enabled =
    open &&
    normalized.length >= 2 &&
    availableModules.length > 0 &&
    claims?.tenantId != null &&
    claims?.userId != null;

  const response = useQuery({
    queryKey: [
      "platform-search",
      claims?.tenantId ?? "none",
      claims?.userId ?? "anon",
      debouncedQuery,
      // Only a narrowed search is a different question from the full one.
      ...(narrowed ? [availableModules.join(",")] : []),
    ],
    enabled: enabled && debouncedQuery.length >= 2,
    retry: false,
    staleTime: 10_000,
    queryFn: ({ signal }) =>
      searchPlatform(
        config,
        {
          query: debouncedQuery,
          limit: 20,
          ...(narrowed ? { modules: availableModules } : {}),
        },
        signal,
      ),
  });

  /* The debounce counts as searching: before it fires, no results would
     otherwise read as "No matches" for the text just typed. */
  const status: PlatformSearchStatus = !enabled
    ? "idle"
    : debouncedQuery !== normalized || response.isFetching
      ? "searching"
      : response.isError
        ? "failed"
        : "done";

  const { data, refetch } = response;
  const retry = useCallback(() => void refetch(), [refetch]);

  /* One object per change, so the shell's search sections are rebuilt only
     when the search moves on. A result that lives in, or opens into, a
     module that is off is dropped: clicking it could only fail. */
  return useMemo(
    () => ({
      results: data?.items
        ? narrowed
          ? data.items.filter(
              (result) =>
                availability.isAvailable(result.ref.module) &&
                availability.isAvailable(result.navigation_target.module),
            )
          : data.items
        : NO_RESULTS,
      partialFailures: data?.partial_failures ?? NO_FAILURES,
      status,
      retry,
    }),
    [availability, data, narrowed, status, retry],
  );
}
